// apps/webapp/src/platform/alert-subscription.ts
import { postSignedToService } from "./post-signed.js";
import { subscriptionCells, type MonitoredRing } from "./monitored-places.js";
import type { Settings } from "./store.js";
import { BUCKETS, type Bucket } from "../screens/now/display-bucket.js";

const SEV_BY_RANK: Record<number, "info" | "warning" | "critical"> = { 1: "info", 2: "warning", 3: "critical" };
const LRANK: Record<string, number> = { info: 1, warning: 2, critical: 3, off: 3 };

/** The wire `minSeverity` is a SINGLE floor the dispatcher applies to every bucket. An unset
 *  per-bucket floor means rank 1 ("info" — alert on everything for that bucket), so the wire
 *  floor is the minimum across ALL buckets (unset → 1). "off" maps to rank 3 so a muted
 *  bucket never lowers the floor (non-regressive: unset defaults to info, rank 1). */
export function minSeverityWire(settings: Settings): "info" | "warning" | "critical" {
  const ranks = BUCKETS.map((b: Bucket) => LRANK[settings.alertLevel?.[b] ?? "info"]);
  return SEV_BY_RANK[Math.min(...ranks)] ?? "info";
}

/** Map numeric quiet hours to the wire {start,end,tz}; undefined when quiet hours are off. */
export function quietHoursWire(q?: { startHour: number; endHour: number }): { start: string; end: string; tz: string } | undefined {
  if (!q) return undefined;
  const hh = (n: number) => `${String(n).padStart(2, "0")}:00`;
  const tz = typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" : "UTC";
  return { start: hh(q.startHour), end: hh(q.endHour), tz };
}

/** Build + POST the signed app.aid.alert.subscription to the dispatcher. Returns false
 *  (no-op) when push creds are absent — the lexicon requires `push`, and without it the
 *  in-app channel is the only alerting path. Re-POST overwrites (dispatcher keys by id).
 *  Throws if the POST itself fails (the caller wraps this in try/catch). */
export async function publishAlertSubscription(args: {
  client: Parameters<typeof postSignedToService>[0];
  rings: MonitoredRing[];
  settings: Settings;
  locale: string;
  push: { endpoint: string; p256dh: string; auth: string } | null;
  postUrl: string;
  fetchImpl?: typeof fetch;
}): Promise<boolean> {
  if (!args.push) return false;
  const cells = subscriptionCells(args.rings);
  if (cells.length === 0) return false;
  const anchorRing = args.rings.find((r) => r.kind !== "live" && r.cell);
  const payload: Record<string, unknown> = {
    cells,
    minSeverity: minSeverityWire(args.settings),
    push: args.push,
    locale: args.locale,
  };
  const bl = Object.entries(args.settings.alertLevel ?? {}).map(([bucket, level]) => ({ bucket, level }));
  if (bl.length) payload.bucketLevels = bl;
  const quiet = quietHoursWire(args.settings.quietHours);
  if (quiet) payload.quietHours = quiet;
  await postSignedToService(
    args.client,
    { $type: "app.aid.alert.subscription", payload, cell: anchorRing!.cell },
    args.postUrl,
    args.fetchImpl ?? fetch,
    "alert.subscription",
  );
  return true;
}
